/* eslint-disable consistent-return */

// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';

import { getProductWeight } from 'shared/helpers/products';

import { getAvailableQuantity, isApplicableItem, mergeSatisfiers } from './common';
import { orderProductsForDiscountPrecedence } from '../utilities';

const formatConditionWeight = (conditionWeight) => {
  if (_.isNil(conditionWeight) || conditionWeight === 'Any Weight' || conditionWeight === 'N/A') {
    return 'any';
  }
  return getProductWeight(conditionWeight);
};

const isEligibleWeight = (conditionWeight, conditionWeightOperator, item) => {
  if (conditionWeight === 'any') {
    return true;
  }
  return conditionWeightOperator === 'greaterThan'
    ? item.weightInGrams > conditionWeight
    : item.weightInGrams === conditionWeight;
};

class Condition {
  constructor(condition, bogoCalcsData, bogoSpecial, items) {
    this.bogoCalcsData = bogoCalcsData;
    this.bogoSpecial = bogoSpecial;
    this.condition = condition;
    this.discountToCartApplicable = !!bogoSpecial.discountToCart?.enabled;
    this.excludeDefeatedRewards = bogoSpecial.itemsForAPrice ?? { enabled: false };
    this.previous = { satisfied: false, satisfiers: {} };
    this.satisfied = false;
    this.satisfiers = {};
    this.weight = formatConditionWeight(condition.weight);
    this.eligibleItems = orderProductsForDiscountPrecedence(
      _.filter(items, (item) => {
        const isApplicable = isApplicableItem(condition, item, bogoSpecial, 'condition');
        const isTotalWeight = !bogoSpecial.totalWeight?.enabled
          ? isEligibleWeight(this.weight, this.condition.weightOperator, item)
          : true;
        return isApplicable && isTotalWeight;
      }),
      bogoSpecial,
      bogoSpecial.itemsForAPrice?.enabled ? 'rewards' : 'conditions'
    );
    this.eligibleItemsHash = _.reduce(
      this.eligibleItems,
      (acc, cur) => {
        acc[cur.key] = cur;
        return acc;
      },
      {}
    );
    this.quantityRemaining = condition?.quantity ?? 0;
  }

  evaluateItem({ item, pendingConditionsSatisfiers, pendingRewardsSatisfiers }) {
    const { _id: conditionId } = this.condition;

    const isEligible = !!this.eligibleItemsHash[item.key];
    if (!isEligible) {
      return { satisfied: this.satisfied, satisfiers: this.satisfiers };
    }

    const availableQuantity = getAvailableQuantity({
      item,
      bogoCalcsData: this.bogoCalcsData,
      pendingConditionsSatisfiers,
      pendingRewardsSatisfiers,
      excludeDefeatedRewardsSatisfiers: this.excludeDefeatedRewards,
      discountToCartApplicable: this.discountToCartApplicable,
      specialId: this.bogoSpecial.specialId,
    });

    if (availableQuantity > 0) {
      const quantityUsed = Math.min(availableQuantity, this.quantityRemaining);
      if (quantityUsed > 0) {
        this.satisfiers = mergeSatisfiers(this.satisfiers, {
          [item.key]: {
            conditions: { [conditionId]: { quantity: quantityUsed, specialId: this.bogoSpecial.specialId } },
            item,
            quantity: quantityUsed,
          },
        });
      }
      this.quantityRemaining -= quantityUsed;
    }

    if (this.quantityRemaining < 1) {
      this.satisfied = true;
    }

    return { satisfied: this.satisfied, satisfiers: this.satisfiers };
  }

  revert() {
    const newPrevious = { satisfied: this.satisfied, satisfiers: this.satisfiers };
    this.satisfied = this.previous.satisfied;
    this.satisfiers = this.previous.satisfiers;
    this.previous = newPrevious;
    this.quantityRemaining = this.condition?.quantity ?? 0;
  }

  reset() {
    const newPrevious = { satisfied: this.satisfied, satisfiers: this.satisfiers };
    this.satisfied = false;
    this.satisfiers = {};
    this.previous = newPrevious;
    this.quantityRemaining = this.condition?.quantity ?? 0;
  }

  setPrevious() {
    this.previous = { satisfied: this.satisfied, satisfiers: this.satisfiers };
  }

  removeSatisfiers(satisfiedCondition) {
    // if we have started assigning satisfiers to conditions that weren't fully satisfied,
    // but then used some of those items to fully satisfy ANOTHER condition, then
    // we want to clean out those half applied ones
    const keys = _.keys(satisfiedCondition.satisfiers);
    const unusedSatisfiers = _.keys(this.satisfiers);

    _.forEach(keys, (key) => {
      if (_.includes(unusedSatisfiers, key)) {
        this.quantityRemaining += satisfiedCondition.satisfiers[key].quantity;
        this.satisfiers[key].quantity -= satisfiedCondition.satisfiers[key].quantity;
        if (this.satisfiers[key].quantity === 0) {
          delete this.satisfiers[key];
        }
      }
    });
  }
}

export default Condition;
